import React, { useEffect, useRef } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { loadLeafletAssets } from "app/utils/leafletLoader";
import {
  ActiveRoutingProviderPayload,
  OPENSTREETMAP_TILE_CONFIG,
  getLeafletTileLayerOptions,
  resolveDashboardMapTileConfig,
} from "app/utils/mapTileConfig";
import { isMapConfigFallbackError } from "app/utils/apiErrorHandler";
import { odooApiFetch } from "app/utils/auth";
import {
  LatLngTuple,
  extendBoundsWithPoints,
  safeRemoveMap,
  toLatLngTuple,
} from "app/utils/routeMapGeo";
import {
  ROUTE_MAP_PIN_ICON_ANCHOR,
  ROUTE_MAP_PIN_ICON_SIZE,
  ROUTE_MAP_PIN_POPUP_ANCHOR,
  RouteMapPinKind,
  createRouteMapPinHtml,
} from "app/utils/routeMapPinMarker";
import {
  MapDeliveryJob,
  MapScreenDriver,
  MapScreenViewMode,
  MapSelectionTool,
  isMapJobSelectable,
} from "app/types/mapScreen";
import { isPointInBounds, isPointInPolygon } from "app/utils/mapSelectionUtils";
import {
  MAP_PIN_TOOLTIP_OPTIONS,
  buildMapPinGroupTooltipHtml,
  buildMapPinTooltipHtml,
  formatMapPinScheduledDate,
} from "app/utils/mapPinTooltip";
import {
  groupMapJobsByLocation,
  isGroupHighlighted,
  isGroupSelected,
  MapJobMarkerGroup,
} from "app/utils/mapJobGroups";
import { MapPanControls, PAN_STEP_PX, PanDirection } from "./MapPanControls";

type LeafletBounds = {
  extend: (coords: LatLngTuple) => LeafletBounds;
  isValid: () => boolean;
};

type LeafletMap = {
  remove: () => void;
  invalidateSize: (animate?: boolean) => void;
  fitBounds: (bounds: LeafletBounds, options?: { padding?: number[] }) => void;
  setView: (coords: LatLngTuple, zoom: number) => void;
  dragging: { disable: () => void; enable: () => void };
  doubleClickZoom: { disable: () => void; enable: () => void };
  getContainer: () => HTMLElement;
  mouseEventToLatLng: (event: MouseEvent) => { lat: number; lng: number };
  containerPointToLatLng: (point: [number, number]) => { lat: number; lng: number };
  panBy: (offset: [number, number], options?: { animate?: boolean }) => void;
  on: (event: string, handler: (event: MouseEvent) => void) => LeafletMap;
  off: (event: string, handler: (event: MouseEvent) => void) => LeafletMap;
};

type LeafletLayer = {
  addTo: (map: LeafletMap) => unknown;
  bindPopup: (html: string) => unknown;
  bindTooltip?: (html: string, options?: Record<string, unknown>) => unknown;
  remove?: () => void;
  setLatLngs?: (coords: LatLngTuple[]) => void;
};

type LeafletStatic = {
  map: (element: HTMLElement, options?: Record<string, unknown>) => LeafletMap;
  control: { zoom: (options?: { position?: string }) => { addTo: (map: LeafletMap) => unknown } };
  tileLayer: (url: string, options?: Record<string, unknown>) => { addTo: (map: LeafletMap) => unknown };
  marker: (
    coords: LatLngTuple,
    options?: Record<string, unknown>
  ) => LeafletLayer & { on: (event: string, handler: () => void) => void; jobId?: string };
  rectangle: (
    bounds: LatLngTuple[] | LeafletBounds,
    options?: Record<string, unknown>
  ) => LeafletLayer;
  polygon: (coords: LatLngTuple[], options?: Record<string, unknown>) => LeafletLayer;
  latLngBounds: (corner1: LatLngTuple, corner2: LatLngTuple) => LeafletBounds;
  divIcon: (options: Record<string, unknown>) => unknown;
};

type LeafletWindow = Window & { L?: LeafletStatic };

const ZONE_STYLE = {
  color: "#5fa67a",
  weight: 2,
  fillColor: "#5fa67a",
  fillOpacity: 0.12,
};

function getApiRoot(): string {
  return (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
}

function getJobPinKind(job: MapDeliveryJob): RouteMapPinKind {
  if (job.status === "unassigned") {
    return "stop-pending";
  }
  if (job.status === "delivered") {
    return "stop-delivered";
  }
  if (job.status === "failed") {
    return "stop-failed";
  }
  return "assigned-stop";
}

function getSelectedPinHtml(sequence?: number | string): string {
  return createRouteMapPinHtml("stop-pending", { sequence }).replace(
    'fill="#f59e0b"',
    'fill="#5fa67a"'
  );
}

function wrapPinWithHighlight(pinHtml: string, isHighlighted: boolean): string {
  if (!isHighlighted) {
    return pinHtml;
  }
  return `<div style="transform:scale(1.18);filter:drop-shadow(0 0 10px rgba(99,102,241,0.85));">${pinHtml}</div>`;
}

function getRepresentativeJob(group: MapJobMarkerGroup): MapDeliveryJob {
  return group.jobs.find((job) => job.status === "unassigned") || group.jobs[0];
}

function getGroupPinHtml(
  group: MapJobMarkerGroup,
  selectedSet: Set<string>,
  isHighlighted: boolean
): string {
  const representative = getRepresentativeJob(group);
  const isSelected = isGroupSelected(group, selectedSet);
  const count = group.jobs.length;
  const label = count > 1 ? count : representative.sequence;

  const inner = isSelected
    ? getSelectedPinHtml(label)
    : createRouteMapPinHtml(getJobPinKind(representative), { sequence: label });

  return wrapPinWithHighlight(inner, isHighlighted);
}

function latLngDistance(a: LatLngTuple, b: LatLngTuple): number {
  const dLat = a[0] - b[0];
  const dLng = a[1] - b[1];
  return Math.sqrt(dLat * dLat + dLng * dLng);
}

function eventToLatLng(map: LeafletMap, event: MouseEvent): LatLngTuple {
  const container = map.getContainer();
  const rect = container.getBoundingClientRect();
  const latlng = map.containerPointToLatLng([
    event.clientX - rect.left,
    event.clientY - rect.top,
  ]);
  return [latlng.lat, latlng.lng];
}

type MapScreenMapProps = {
  accessToken: string;
  active: boolean;
  viewMode: MapScreenViewMode;
  jobs: MapDeliveryJob[];
  liveDrivers: MapScreenDriver[];
  selectedJobIds: string[];
  highlightedJobId: string | null;
  selectionTool: MapSelectionTool;
  userTimezone: string;
  onToggleJobs: (jobIds: string[]) => void;
  onSelectJobs: (jobIds: string[]) => void;
  focusedRouteId?: number | null;
};

export function MapScreenMap({
  accessToken,
  active,
  viewMode,
  jobs,
  liveDrivers,
  selectedJobIds,
  highlightedJobId,
  selectionTool,
  userTimezone,
  onToggleJobs,
  onSelectJobs,
  focusedRouteId,
}: MapScreenMapProps) {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<LeafletMap | null>(null);
  const markerRefs = useRef<
    Array<LeafletLayer & { jobId?: string; on?: (event: string, handler: () => void) => void }>
  >([]);
  const driverMarkerRefs = useRef<LeafletLayer[]>([]);
  const overlayRef = useRef<LeafletLayer | null>(null);
  const drawStartRef = useRef<LatLngTuple | null>(null);
  const lassoPointsRef = useRef<LatLngTuple[]>([]);
  const isDrawingRef = useRef(false);
  const selectionToolRef = useRef(selectionTool);
  const onSelectJobsRef = useRef(onSelectJobs);
  const onToggleJobsRef = useRef(onToggleJobs);
  const selectableJobsRef = useRef<MapDeliveryJob[]>([]);
  const userTimezoneRef = useRef(userTimezone);
  const lastFitBoundsKeyRef = useRef<string | null>(null);
  const spaceHeldRef = useRef(false);
  const documentListenersRef = useRef<{
    mousemove: (event: MouseEvent) => void;
    mouseup: (event: MouseEvent) => void;
  } | null>(null);
  const [status, setStatus] = React.useState<"loading" | "ready" | "error">("loading");

  selectionToolRef.current = selectionTool;
  onSelectJobsRef.current = onSelectJobs;
  onToggleJobsRef.current = onToggleJobs;
  userTimezoneRef.current = userTimezone;

  const visibleJobs = React.useMemo(() => {
    if (focusedRouteId) {
      return jobs.filter((job) => job.routeId === focusedRouteId);
    }
    return jobs;
  }, [focusedRouteId, jobs]);

  selectableJobsRef.current = visibleJobs.filter(isMapJobSelectable);
  const selectedSet = React.useMemo(() => new Set(selectedJobIds), [selectedJobIds]);
  const zoneSelectionActive = viewMode === "select" && selectionTool !== "pointer";

  const markerGroups = React.useMemo(
    () => groupMapJobsByLocation(visibleJobs),
    [visibleJobs]
  );

  const fitBoundsKey = React.useMemo(() => {
    const jobKey = visibleJobs.map((job) => job.id).join("|");
    return `${viewMode}:${focusedRouteId ?? "all"}:${jobKey}`;
  }, [focusedRouteId, viewMode, visibleJobs]);

  useEffect(() => {
    if (!active || !accessToken || !mapContainerRef.current) {
      return;
    }

    let cancelled = false;

    const initMap = async () => {
      setStatus("loading");
      try {
        await loadLeafletAssets();
        const L = (window as LeafletWindow).L;
        if (!L || cancelled || !mapContainerRef.current) {
          return;
        }

        let mapConfig = resolveDashboardMapTileConfig(undefined);
        try {
          const activeProvider = await odooApiFetch<ActiveRoutingProviderPayload>(
            `${getApiRoot()}/routing/active_provider`,
            accessToken
          );
          mapConfig = resolveDashboardMapTileConfig(activeProvider);
        } catch (configError) {
          if (!isMapConfigFallbackError(configError)) {
            throw configError;
          }
        }

        safeRemoveMap(mapRef.current);
        mapRef.current = null;
        mapContainerRef.current.replaceChildren();
        delete (mapContainerRef.current as HTMLElement & { _leaflet_id?: number })._leaflet_id;

        const map = L.map(mapContainerRef.current, { zoomControl: false });
        L.control.zoom({ position: "bottomleft" }).addTo(map);
        mapRef.current = map as LeafletMap;

        const tileUrl =
          mapConfig.tile_url_template || OPENSTREETMAP_TILE_CONFIG.tile_url_template!;
        L.tileLayer(tileUrl, getLeafletTileLayerOptions(mapConfig)).addTo(map);

        if (!cancelled) {
          setStatus("ready");
        }
      } catch {
        if (!cancelled) {
          setStatus("error");
        }
      }
    };

    void initMap();

    return () => {
      cancelled = true;
      safeRemoveMap(mapRef.current);
      mapRef.current = null;
    };
  }, [accessToken, active]);

  useEffect(() => {
    const map = mapRef.current;
    const L = (window as LeafletWindow).L;
    if (!map || !L || status !== "ready") {
      return;
    }

    markerRefs.current.forEach((marker) => marker.remove?.());
    markerRefs.current = [];
    driverMarkerRefs.current.forEach((marker) => marker.remove?.());
    driverMarkerRefs.current = [];

    const boundsPoints: LatLngTuple[] = [];

    for (const group of markerGroups) {
      const point: LatLngTuple = [group.lat, group.lng];
      const isHighlighted = isGroupHighlighted(group, highlightedJobId);
      const hasSelectable = group.jobs.some(isMapJobSelectable);
      const marker = L.marker(point, {
        icon: L.divIcon({
          className: "route-map-pin-leaflet-icon",
          html: getGroupPinHtml(group, selectedSet, isHighlighted),
          iconSize: ROUTE_MAP_PIN_ICON_SIZE,
          iconAnchor: ROUTE_MAP_PIN_ICON_ANCHOR,
          popupAnchor: ROUTE_MAP_PIN_POPUP_ANCHOR,
        }),
        interactive: true,
        zIndexOffset: isHighlighted ? 1000 : 0,
      }) as LeafletLayer & {
        groupId?: string;
        jobIds?: string[];
        on?: (event: string, handler: () => void) => void;
      };

      marker.groupId = group.id;
      marker.jobIds = group.jobs.map((job) => job.id);

      const tooltipHtml =
        group.jobs.length > 1
          ? buildMapPinGroupTooltipHtml(group.jobs, userTimezoneRef.current)
          : buildMapPinTooltipHtml({
              pickingNumber: group.jobs[0].pickingNumber || group.jobs[0].name,
              partnerName: group.jobs[0].partnerName || group.jobs[0].customerName,
              address: group.jobs[0].address,
              status: group.jobs[0].status,
              routeName: group.jobs[0].routeName,
              scheduledDateLabel: formatMapPinScheduledDate(
                group.jobs[0].scheduledDate,
                userTimezoneRef.current
              ),
            });
      marker.bindTooltip?.(tooltipHtml, MAP_PIN_TOOLTIP_OPTIONS);

      if (viewMode === "select" && hasSelectable) {
        marker.on?.("click", () => {
          if (selectionToolRef.current !== "pointer") {
            return;
          }
          const selectableIds = group.jobs
            .filter(isMapJobSelectable)
            .map((job) => job.id);
          if (selectableIds.length > 0) {
            onToggleJobsRef.current(selectableIds);
          }
        });
      }

      marker.addTo(map);
      markerRefs.current.push(marker);
      boundsPoints.push(point);
    }

    if (viewMode === "live") {
      for (const driver of liveDrivers) {
        const point = toLatLngTuple(driver.lat, driver.lng);
        if (!point) {
          continue;
        }
        const marker = L.marker(point, {
          icon: L.divIcon({
            className: "route-map-pin-leaflet-icon",
            html: createRouteMapPinHtml("driver"),
            iconSize: ROUTE_MAP_PIN_ICON_SIZE,
            iconAnchor: ROUTE_MAP_PIN_ICON_ANCHOR,
            popupAnchor: ROUTE_MAP_PIN_POPUP_ANCHOR,
          }),
        }) as LeafletLayer;
        marker.bindPopup(
          `<strong>${driver.name}</strong><br/>${
            driver.routeName ? `Route: ${driver.routeName}<br/>` : ""
          }${driver.isOnDuty ? "On duty" : "Off duty"}`
        );
        marker.addTo(map);
        driverMarkerRefs.current.push(marker);
        boundsPoints.push(point);
      }
    }

    if (boundsPoints.length > 0 && lastFitBoundsKeyRef.current !== fitBoundsKey) {
      let mapBounds = L.latLngBounds(boundsPoints[0], boundsPoints[0]);
      extendBoundsWithPoints(mapBounds, boundsPoints);
      map.fitBounds(mapBounds, { padding: [48, 48] });
      lastFitBoundsKeyRef.current = fitBoundsKey;
    } else if (boundsPoints.length === 0 && lastFitBoundsKeyRef.current !== fitBoundsKey) {
      map.setView([40.7128, -74.006], 5);
      lastFitBoundsKeyRef.current = fitBoundsKey;
    }

    window.setTimeout(() => map.invalidateSize(true), 150);
  }, [
    fitBoundsKey,
    highlightedJobId,
    liveDrivers,
    markerGroups,
    selectedSet,
    status,
    userTimezone,
    viewMode,
    visibleJobs,
  ]);

  const removeOverlayLayer = React.useCallback(() => {
    overlayRef.current?.remove?.();
    overlayRef.current = null;
  }, []);

  const detachDocumentListeners = React.useCallback(() => {
    const listeners = documentListenersRef.current;
    if (!listeners) {
      return;
    }
    document.removeEventListener("mousemove", listeners.mousemove);
    document.removeEventListener("mouseup", listeners.mouseup);
    documentListenersRef.current = null;
  }, []);

  const finishZoneDraw = React.useCallback(() => {
    const map = mapRef.current;
    if (!map) {
      return;
    }

    map.dragging.enable();
    map.doubleClickZoom.enable();
    isDrawingRef.current = false;
    drawStartRef.current = null;
    detachDocumentListeners();
    window.setTimeout(removeOverlayLayer, 300);
  }, [detachDocumentListeners, removeOverlayLayer]);

  const selectJobsInArea = React.useCallback(
    (predicate: (point: LatLngTuple) => boolean) => {
      const ids = selectableJobsRef.current
        .filter((job) => predicate([job.lat, job.lng]))
        .map((job) => job.id);
      if (ids.length > 0) {
        onSelectJobsRef.current(ids);
      }
    },
    []
  );

  const handleZoneDrawStart = React.useCallback(
    (event: MouseEvent) => {
      const tool = selectionToolRef.current;
      const map = mapRef.current;
      const L = (window as LeafletWindow).L;

      if (tool === "pointer" || event.button !== 0 || !map || !L) {
        return;
      }

      if (spaceHeldRef.current) {
        return;
      }

      const target = event.target as HTMLElement | null;
      if (target?.closest(".leaflet-control, .map-screen-overlay-panel")) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const start = eventToLatLng(map, event);
      drawStartRef.current = start;
      isDrawingRef.current = true;
      lassoPointsRef.current = tool === "lasso" ? [start] : [];

      removeOverlayLayer();
      map.dragging.disable();
      map.doubleClickZoom.disable();

      if (tool === "lasso") {
        overlayRef.current = L.polygon(lassoPointsRef.current, ZONE_STYLE).addTo(
          map
        ) as LeafletLayer;
      }

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!isDrawingRef.current || !drawStartRef.current) {
          return;
        }

        const activeTool = selectionToolRef.current;
        const activeMap = mapRef.current;
        const leaflet = (window as LeafletWindow).L;
        if (!activeMap || !leaflet) {
          return;
        }

        moveEvent.preventDefault();
        const current = eventToLatLng(activeMap, moveEvent);
        const drawStart = drawStartRef.current;

        if (activeTool === "rectangle") {
          removeOverlayLayer();
          overlayRef.current = leaflet
            .rectangle(leaflet.latLngBounds(drawStart, current), ZONE_STYLE)
            .addTo(activeMap) as LeafletLayer;
        }

        if (activeTool === "lasso") {
          const last = lassoPointsRef.current[lassoPointsRef.current.length - 1];
          if (!last || latLngDistance(last, current) > 0.00008) {
            lassoPointsRef.current = [...lassoPointsRef.current, current];
            overlayRef.current?.setLatLngs?.(lassoPointsRef.current);
          }
        }
      };

      const handleMouseUp = (upEvent: MouseEvent) => {
        if (!isDrawingRef.current || !drawStartRef.current) {
          finishZoneDraw();
          return;
        }

        const activeTool = selectionToolRef.current;
        const activeMap = mapRef.current;
        if (!activeMap) {
          finishZoneDraw();
          return;
        }

        const end = eventToLatLng(activeMap, upEvent);
        const drawStart = drawStartRef.current;

        if (activeTool === "rectangle") {
          selectJobsInArea((point) => isPointInBounds(point, drawStart, end));
        }

        if (activeTool === "lasso" && lassoPointsRef.current.length >= 3) {
          const polygon = [...lassoPointsRef.current, lassoPointsRef.current[0]];
          selectJobsInArea((point) => isPointInPolygon(point, polygon));
        }

        lassoPointsRef.current = [];
        finishZoneDraw();
      };

      detachDocumentListeners();
      documentListenersRef.current = {
        mousemove: handleMouseMove,
        mouseup: handleMouseUp,
      };
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [detachDocumentListeners, finishZoneDraw, removeOverlayLayer, selectJobsInArea]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === "Space") {
        spaceHeldRef.current = true;
      }
    };
    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.code === "Space") {
        spaceHeldRef.current = false;
      }
    };
    const handleBlur = () => {
      spaceHeldRef.current = false;
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleBlur);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleBlur);
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || status !== "ready" || !zoneSelectionActive) {
      return;
    }

    const container = map.getContainer();
    container.addEventListener("mousedown", handleZoneDrawStart, { capture: true });

    return () => {
      container.removeEventListener("mousedown", handleZoneDrawStart, { capture: true });
    };
  }, [handleZoneDrawStart, status, zoneSelectionActive]);

  useEffect(() => {
    return () => {
      detachDocumentListeners();
      removeOverlayLayer();
      mapRef.current?.dragging.enable();
      mapRef.current?.doubleClickZoom.enable();
    };
  }, [detachDocumentListeners, removeOverlayLayer]);

  useEffect(() => {
    if (!zoneSelectionActive) {
      finishZoneDraw();
    }
  }, [finishZoneDraw, zoneSelectionActive]);

  const handleMapPan = React.useCallback((direction: PanDirection) => {
    const map = mapRef.current;
    if (!map) {
      return;
    }

    const step = PAN_STEP_PX;
    const offsetByDirection: Record<PanDirection, [number, number]> = {
      up: [0, -step],
      down: [0, step],
      left: [-step, 0],
      right: [step, 0],
    };

    map.panBy(offsetByDirection[direction], { animate: true });
  }, []);

  useEffect(() => {
    if (!mapContainerRef.current) {
      return;
    }
    const invalidate = () => {
      window.setTimeout(() => mapRef.current?.invalidateSize(true), 100);
    };
    const observer = new ResizeObserver(invalidate);
    observer.observe(mapContainerRef.current);
    return () => observer.disconnect();
  }, [status]);

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        bgcolor: "#e5e7eb",
        "& .route-map-pin-leaflet-icon": {
          background: "transparent",
          border: "none",
          pointerEvents: zoneSelectionActive ? "none" : "auto",
        },
        "& .leaflet-container": {
          cursor: zoneSelectionActive ? "crosshair" : "grab",
        },
        "& .leaflet-control-zoom": {
          pointerEvents: "auto",
        },
        "& .route-map-pin-tooltip": {
          background: "rgba(255,255,255,0.98)",
          border: "1px solid #e5e9ef",
          borderRadius: "10px",
          boxShadow: "0 8px 20px rgba(15, 23, 42, 0.12)",
          padding: "8px 10px 12px",
          color: "#334155",
          pointerEvents: "auto",
          "&::before": {
            borderTopColor: "#e5e9ef",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            left: 0,
            right: 0,
            bottom: -12,
            height: 12,
          },
        },
      }}
    >
      <Box ref={mapContainerRef} sx={{ position: "absolute", inset: 0 }} />
      {zoneSelectionActive && status === "ready" && (
        <MapPanControls onPan={handleMapPan} />
      )}
      {status === "loading" && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(255,255,255,0.72)",
            zIndex: 400,
          }}
        >
          <CircularProgress size={32} sx={{ color: "#7ec597" }} />
        </Box>
      )}
      {status === "error" && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#fff",
            zIndex: 400,
          }}
        >
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            Map unavailable
          </Typography>
        </Box>
      )}
    </Box>
  );
}
